// src/pages/RestaurantOwner/ProfitLoss.jsx - FIXED WITH REAL FIREBASE DATA

import React, { useState, useEffect, useMemo } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Package,
  AlertCircle,
  Calendar,
  Download,
  PieChart,
  BarChart3,
  Loader,
  Target,
  Award,
  Info,
  Plus,
  Edit,
  Trash2,
  X,
  Users,
  Home,
  FileText,
  CheckCircle  
} from 'lucide-react';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import StorageService from '../../services/StorageService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart as RechartsBar, Bar, Cell } from 'recharts';

const ProfitLoss = ({ restaurantId }) => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [revenue, setRevenue] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [operatingExpenses, setOperatingExpenses] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  
  // Operating Expense Modal State
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseType, setExpenseType] = useState('salary');
  const [expenseForm, setExpenseForm] = useState({
    type: 'salary',
    employeeName: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    description: '',
    month: new Date().toISOString().slice(0, 7)
  });
  const [editingExpense, setEditingExpense] = useState(null);
  const [saving, setSaving] = useState(false);

  // Safe number helper - FIXES NaN ISSUES
  const safeNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  useEffect(() => {
    loadProfitLossData();
  }, [restaurantId, dateRange]);

  const loadProfitLossData = async () => {
    try {
      setLoading(true);

      // Load restaurant data
      const rest = await StorageService.getRestaurant(restaurantId);
      setRestaurant(rest);

      // Load invoices (revenue) - REAL DATA
      const invoicesSnapshot = await getDocs(
        collection(db, 'restaurants', restaurantId, 'invoices')
      );
      const invoicesData = invoicesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(inv => !inv.cancelled); // Exclude cancelled invoices
      setRevenue(invoicesData);

      // Load purchases (COGS) - REAL DATA
      const purchasesSnapshot = await getDocs(
        collection(db, 'restaurants', restaurantId, 'inventory_purchases')
      );
      const purchasesData = purchasesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPurchases(purchasesData);

      // Load operating expenses - REAL DATA
      const expensesSnapshot = await getDocs(
        collection(db, 'restaurants', restaurantId, 'operating_expenses')
      );
      const expensesData = expensesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOperatingExpenses(expensesData);

      console.log('✅ P&L data loaded:', {
        invoices: invoicesData.length,
        purchases: purchasesData.length,
        expenses: expensesData.length
      });
    } catch (error) {
      console.error('❌ Error loading P&L data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    const now = new Date();
    let startDate = new Date();

    if (dateRange === '7') startDate.setDate(now.getDate() - 7);
    else if (dateRange === '30') startDate.setDate(now.getDate() - 30);
    else if (dateRange === '90') startDate.setDate(now.getDate() - 90);
    else if (dateRange === '365') startDate.setDate(now.getDate() - 365);

    const filteredRevenue = revenue.filter(inv => {
      const invDate = new Date(inv.timestamp);
      return invDate >= startDate && invDate <= now;
    });

    const filteredPurchases = purchases.filter(p => {
      const pDate = new Date(p.purchaseDate);
      return pDate >= startDate && pDate <= now;
    });

    const filteredOpEx = operatingExpenses.filter(e => {
      const eDate = new Date(e.date);
      return eDate >= startDate && eDate <= now;
    });

    // Calculate totals with safe number conversion - FIXES NaN
    const totalRevenue = filteredRevenue.reduce((sum, inv) => sum + safeNumber(inv.grandTotal), 0);
    const totalCOGS = filteredPurchases.reduce((sum, p) => sum + safeNumber(p.grandTotal), 0);
    const totalOpEx = filteredOpEx.reduce((sum, e) => sum + safeNumber(e.amount), 0);

    const grossProfit = totalRevenue - totalCOGS;
    const netProfit = grossProfit - totalOpEx;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
    const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

    // Monthly breakdown
    const monthlyData = {};
    
    filteredRevenue.forEach(inv => {
      const month = new Date(inv.timestamp).toLocaleString('default', { month: 'short', year: '2-digit' });
      if (!monthlyData[month]) {
        monthlyData[month] = { month, revenue: 0, cogs: 0, opex: 0, grossProfit: 0, netProfit: 0 };
      }
      monthlyData[month].revenue += safeNumber(inv.grandTotal);
    });

    filteredPurchases.forEach(p => {
      const month = new Date(p.purchaseDate).toLocaleString('default', { month: 'short', year: '2-digit' });
      if (!monthlyData[month]) {
        monthlyData[month] = { month, revenue: 0, cogs: 0, opex: 0, grossProfit: 0, netProfit: 0 };
      }
      monthlyData[month].cogs += safeNumber(p.grandTotal);
    });

    filteredOpEx.forEach(e => {
      const month = new Date(e.date).toLocaleString('default', { month: 'short', year: '2-digit' });
      if (!monthlyData[month]) {
        monthlyData[month] = { month, revenue: 0, cogs: 0, opex: 0, grossProfit: 0, netProfit: 0 };
      }
      monthlyData[month].opex += safeNumber(e.amount);
    });

    Object.values(monthlyData).forEach(month => {
      month.grossProfit = month.revenue - month.cogs;
      month.netProfit = month.grossProfit - month.opex;
    });

    const monthlyArray = Object.values(monthlyData).sort((a, b) => {
      const dateA = new Date(a.month);
      const dateB = new Date(b.month);
      return dateA - dateB;
    });

    // Expense breakdown
    const expenseBreakdown = [
      { name: 'Cost of Goods (Inventory)', value: totalCOGS, color: '#f97316' },
      { name: 'Operating Expenses', value: totalOpEx, color: '#ef4444' }
    ];

    // Category-wise COGS
    const categoryWiseCOGS = {};
    filteredPurchases.forEach(purchase => {
      (purchase.items || []).forEach(item => {
        const cat = item.category || 'Other';
        if (!categoryWiseCOGS[cat]) categoryWiseCOGS[cat] = 0;
        categoryWiseCOGS[cat] += safeNumber(item.totalAmount);
      });
    });

    // Operating expense breakdown
    const salaryExpenses = filteredOpEx.filter(e => e.type === 'salary').reduce((sum, e) => sum + safeNumber(e.amount), 0);
    const rentExpenses = filteredOpEx.filter(e => e.type === 'rent').reduce((sum, e) => sum + safeNumber(e.amount), 0);
    const miscExpenses = filteredOpEx.filter(e => e.type === 'misc').reduce((sum, e) => sum + safeNumber(e.amount), 0);

    return {
      totalRevenue,
      totalCOGS,
      totalOpEx,
      grossProfit,
      netProfit,
      profitMargin,
      grossMargin,
      monthlyArray,
      expenseBreakdown,
      categoryWiseCOGS: Object.entries(categoryWiseCOGS).map(([name, value]) => ({ name, value })),
      revenueCount: filteredRevenue.length,
      avgOrderValue: filteredRevenue.length > 0 ? totalRevenue / filteredRevenue.length : 0,
      salaryExpenses,
      rentExpenses,
      miscExpenses
    };
  }, [revenue, purchases, operatingExpenses, dateRange]);

  const openExpenseModal = (type = 'salary', expense = null) => {
    setExpenseType(type);
    if (expense) {
      setEditingExpense(expense);
      setExpenseForm({
        type: expense.type,
        employeeName: expense.employeeName || '',
        amount: safeNumber(expense.amount),
        date: expense.date || new Date().toISOString().split('T')[0],
        description: expense.description || '',
        month: expense.month || new Date().toISOString().slice(0, 7)
      });
    } else {
      setExpenseForm({
        type: type,
        employeeName: '',
        amount: 0,
        date: new Date().toISOString().split('T')[0],
        description: '',
        month: new Date().toISOString().slice(0, 7)
      });
    }
    setShowExpenseModal(true);
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    
    if (expenseForm.amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      setSaving(true);

      const expenseData = {
        ...expenseForm,
        amount: safeNumber(expenseForm.amount),
        updatedAt: new Date().toISOString()
      };

      if (editingExpense) {
        // Update existing expense
        const expenseRef = doc(db, 'restaurants', restaurantId, 'operating_expenses', editingExpense.id);
        await updateDoc(expenseRef, expenseData);
        console.log('✅ Expense updated');
      } else {
        // Add new expense
        const newId = `exp_${Date.now()}`;
        const expenseRef = doc(db, 'restaurants', restaurantId, 'operating_expenses', newId);
        await setDoc(expenseRef, {
          id: newId,
          ...expenseData,
          createdAt: new Date().toISOString()
        });
        console.log('✅ Expense added');
      }

      setShowExpenseModal(false);
      setEditingExpense(null);
      await loadProfitLossData();
      StorageService.clearCache();
      alert(editingExpense ? '✅ Expense updated!' : '✅ Expense added!');
    } catch (error) {
      console.error('❌ Error saving expense:', error);
      alert('Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;

    try {
      setSaving(true);
      await deleteDoc(doc(db, 'restaurants', restaurantId, 'operating_expenses', expenseId));
      await loadProfitLossData();
      StorageService.clearCache();
      console.log('✅ Expense deleted');
      alert('✅ Expense deleted!');
    } catch (error) {
      console.error('❌ Error deleting expense:', error);
      alert('Failed to delete expense');
    } finally {
      setSaving(false);
    }
  };

  const exportPLStatement = () => {
    const headers = ['Metric', 'Amount (₹)'];
    const rows = [
      ['REVENUE', ''],
      ['Total Sales', filteredData.totalRevenue.toFixed(2)],
      ['Number of Orders', filteredData.revenueCount],
      ['Average Order Value', filteredData.avgOrderValue.toFixed(2)],
      ['', ''],
      ['COST OF GOODS SOLD (COGS)', ''],
      ['Inventory Purchases', filteredData.totalCOGS.toFixed(2)],
      ['', ''],
      ['GROSS PROFIT', filteredData.grossProfit.toFixed(2)],
      ['Gross Margin %', filteredData.grossMargin.toFixed(2) + '%'],
      ['', ''],
      ['OPERATING EXPENSES', ''],
      ['Employee Salaries', filteredData.salaryExpenses.toFixed(2)],
      ['Rent', filteredData.rentExpenses.toFixed(2)],
      ['Miscellaneous', filteredData.miscExpenses.toFixed(2)],
      ['Total Operating Expenses', filteredData.totalOpEx.toFixed(2)],
      ['', ''],
      ['NET PROFIT', filteredData.netProfit.toFixed(2)],
      ['Net Profit Margin %', filteredData.profitMargin.toFixed(2) + '%']
    ];

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `PL_Statement_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Profit & Loss data...</p>
        </div>
      </div>
    );
  }

if (!loading && restaurant) {
    const plan = restaurant.package?.toLowerCase();
    const hasPLAccess = plan === 'premium' || plan === 'advanced' || plan==='basic';
    if (!hasPLAccess) {
        return (
            <div className="p-6 min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
                <div className="max-w-2xl mx-auto mt-20">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center border-4 border-green-200">
                        <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <DollarSign className="w-10 h-10 text-red-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">P&amp;L Statement Access Restricted</h2>
                        <p className="text-lg text-gray-600 mb-2">
                            You are currently on the <span className="font-bold text-orange-600">{restaurant.package || 'Basic'} Plan</span>
                        </p>
                        <p className="text-gray-600 mb-8">
                            Profit &amp; Loss Statement is only available for <span className="font-bold">Premium</span> and <span className="font-bold">Advanced</span> plan users.
                        </p>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-8">
                            <h3 className="font-bold text-gray-800 mb-4">Upgrade to access:</h3>
                            <ul className="text-left space-y-2 text-gray-700">
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Complete Profit &amp; Loss Statement
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Revenue, COGS &amp; Operating Expenses Tracking
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Gross Profit &amp; Net Profit Analysis
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Monthly Profit Trends
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Expense Management (Salary, Rent, Misc)
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600"/>
                                    Business Insights &amp; Recommendations
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Exportable Financial Reports
                                </li>
                            </ul>
                        </div>
                        <a
                            href={'mailto:contact@zyppro.com?subject=Upgrade%20to%20Premium%20Plan&body=Hi%20Zyppro%20Team,%0D%0A%0D%0AI%20would%20like%20to%20upgrade%20to%20Premium%20or%20Advanced%20plan%20to%20access%20Profit%20%26%20Loss%20Statement.%0D%0A%0D%0ARestaurant:%20'+ encodeURIComponent(restaurant.name) + '%0D%0ACurrent%20Plan:%20' + encodeURIComponent(restaurant.package || 'Basic') +'%0D%0A%0D%0APlease%20contact%20me.%0D%0A%0D%0AThank%20you'}
                            className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-600 transition shadow-lg"
                        >
                            <Award className="w-6 h-6" />
                            Contact Us to Upgrade
                        </a>
                        <p className="text-sm text-gray-600 mt-4">
                            Email: <a href="mailto:contact@zyppro.com" className="text-green-600 underline font-semibold">contact@zyppro.com</a>
                        </p>
                    </div>
                </div>
            </div>
        );
    }
}

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-orange-50 to-yellow-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <DollarSign className="w-8 h-8 text-green-600" />
            Profit & Loss Statement
          </h1>
          <p className="text-gray-600 text-sm mt-1">Understand your business profitability</p>
        </div>

        <div className="flex gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="365">Last Year</option>
          </select>

          <button
            onClick={exportPLStatement}
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-600 text-sm">Total Revenue</p>
            <ShoppingCart className="w-8 h-8 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-800">₹{filteredData.totalRevenue.toFixed(0)}</p>
          <p className="text-xs text-gray-500 mt-1">{filteredData.revenueCount} orders</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-600 text-sm">Cost of Goods</p>
            <Package className="w-8 h-8 text-orange-500" />
          </div>
          <p className="text-3xl font-bold text-gray-800">₹{filteredData.totalCOGS.toFixed(0)}</p>
          <p className="text-xs text-gray-500 mt-1">Inventory purchases</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-600 text-sm">Gross Profit</p>
            <TrendingUp className="w-8 h-8 text-purple-500" />
          </div>
          <p className={`text-3xl font-bold ${filteredData.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ₹{filteredData.grossProfit.toFixed(0)}
          </p>
          <p className="text-xs text-gray-500 mt-1">{filteredData.grossMargin.toFixed(1)}% margin</p>
        </div>

        <div className={`rounded-xl shadow-md p-6 border-l-4 ${
          filteredData.netProfit >= 0 ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-500' : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-500'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-600 text-sm font-semibold">NET PROFIT</p>
            {filteredData.netProfit >= 0 ? (
              <Award className="w-8 h-8 text-green-600" />
            ) : (
              <AlertCircle className="w-8 h-8 text-red-600" />
            )}
          </div>
          <p className={`text-4xl font-bold ${filteredData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ₹{filteredData.netProfit.toFixed(0)}
          </p>
          <p className="text-xs text-gray-600 mt-1 font-medium">
            {filteredData.profitMargin.toFixed(1)}% profit margin
          </p>
        </div>
      </div>

      {/* Operating Expenses Quick Add */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">Operating Expenses Management</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <button
            onClick={() => openExpenseModal('salary')}
            className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition shadow-md"
          >
            <Users className="w-6 h-6" />
            <div className="text-left">
              <p className="font-bold">Add Employee Salary</p>
              <p className="text-xs text-blue-100">Total: ₹{filteredData.salaryExpenses.toFixed(0)}</p>
            </div>
          </button>

          <button
            onClick={() => openExpenseModal('rent')}
            className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition shadow-md"
          >
            <Home className="w-6 h-6" />
            <div className="text-left">
              <p className="font-bold">Add Rent</p>
              <p className="text-xs text-purple-100">Total: ₹{filteredData.rentExpenses.toFixed(0)}</p>
            </div>
          </button>

          <button
            onClick={() => openExpenseModal('misc')}
            className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition shadow-md"
          >
            <FileText className="w-6 h-6" />
            <div className="text-left">
              <p className="font-bold">Add Misc. Expense</p>
              <p className="text-xs text-orange-100">Total: ₹{filteredData.miscExpenses.toFixed(0)}</p>
            </div>
          </button>
        </div>

        {/* Expense List */}
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-700 mb-3">Recent Expenses</h4>
          {operatingExpenses.length > 0 ? (
            operatingExpenses.slice(0, 10).map(expense => (
              <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                <div className="flex items-center gap-3">
                  {expense.type === 'salary' && <Users className="w-5 h-5 text-blue-500" />}
                  {expense.type === 'rent' && <Home className="w-5 h-5 text-purple-500" />}
                  {expense.type === 'misc' && <FileText className="w-5 h-5 text-orange-500" />}
                  <div>
                    <p className="font-semibold text-gray-800">
                      {expense.type === 'salary' ? expense.employeeName : expense.description}
                    </p>
                    <p className="text-xs text-gray-500">
                      {expense.type.charAt(0).toUpperCase() + expense.type.slice(1)} • {new Date(expense.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-lg font-bold text-gray-800">₹{safeNumber(expense.amount).toFixed(0)}</p>
                  <button
                    onClick={() => openExpenseModal(expense.type, expense)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteExpense(expense.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                    disabled={saving}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No operating expenses recorded yet</p>
              <p className="text-xs mt-1">Click the buttons above to add expenses</p>
            </div>
          )}
        </div>
      </div>

      {/* Profit Breakdown Formula */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Info className="w-6 h-6 text-blue-500" />
          Profit Calculation Breakdown
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <span className="font-semibold text-gray-700">Revenue (Sales)</span>
            <span className="text-xl font-bold text-blue-600">₹{filteredData.totalRevenue.toFixed(0)}</span>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-gray-400 font-bold text-2xl">−</div>
          </div>

          <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
            <span className="font-semibold text-gray-700">Cost of Goods Sold (COGS)</span>
            <span className="text-xl font-bold text-orange-600">₹{filteredData.totalCOGS.toFixed(0)}</span>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-gray-400 font-bold text-2xl">=</div>
          </div>

          <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
            <span className="font-bold text-gray-800">GROSS PROFIT</span>
            <span className="text-2xl font-bold text-purple-600">₹{filteredData.grossProfit.toFixed(0)}</span>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-gray-400 font-bold text-2xl">−</div>
          </div>

          <div className="space-y-2 p-4 bg-red-50 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">Salaries:</span>
              <span className="font-bold text-red-600">₹{filteredData.salaryExpenses.toFixed(0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">Rent:</span>
              <span className="font-bold text-red-600">₹{filteredData.rentExpenses.toFixed(0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">Miscellaneous:</span>
              <span className="font-bold text-red-600">₹{filteredData.miscExpenses.toFixed(0)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-red-200">
              <span className="font-semibold text-gray-700">Total Operating Expenses:</span>
              <span className="text-xl font-bold text-red-600">₹{filteredData.totalOpEx.toFixed(0)}</span>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-gray-400 font-bold text-2xl">=</div>
          </div>

          <div className={`flex items-center justify-between p-6 rounded-lg border-4 ${
            filteredData.netProfit >= 0 ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
          }`}>
            <span className="font-bold text-gray-800 text-xl">NET PROFIT</span>
            <span className={`text-3xl font-bold ${filteredData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ₹{filteredData.netProfit.toFixed(0)}
            </span>
          </div>
        </div>

        {filteredData.netProfit < 0 && (
          <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-800 font-semibold flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Your business is running at a loss!
            </p>
            <p className="text-red-700 text-sm mt-2">
              Consider: Increasing prices, reducing COGS, cutting operating expenses, or boosting sales volume.
            </p>
          </div>
        )}
      </div>

      {/* Monthly Trend */}
      {filteredData.monthlyArray.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Monthly Profit Trend</h3>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={filteredData.monthlyArray}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => '₹' + value.toFixed(0)} />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} name="Revenue" />
              <Line type="monotone" dataKey="cogs" stroke="#f97316" strokeWidth={2} name="COGS" />
              <Line type="monotone" dataKey="grossProfit" stroke="#8b5cf6" strokeWidth={2} name="Gross Profit" />
              <Line type="monotone" dataKey="netProfit" stroke="#10b981" strokeWidth={3} name="Net Profit" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Expense Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Expense Distribution</h3>
          <div className="space-y-4">
            {filteredData.expenseBreakdown.map((expense, idx) => {
              const total = filteredData.totalCOGS + filteredData.totalOpEx;
              const percentage = total > 0 ? (expense.value / total) * 100 : 0;
              
              return (
                <div key={idx}>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 font-medium">{expense.name}</span>
                    <span className="font-bold" style={{ color: expense.color }}>
                      ₹{expense.value.toFixed(0)} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="h-3 rounded-full transition-all"
                      style={{ width: `${percentage}%`, backgroundColor: expense.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-4 border-t">
            <div className="flex justify-between items-center">
              <span className="font-bold text-gray-800">Total Expenses</span>
              <span className="text-2xl font-bold text-red-600">
                ₹{(filteredData.totalCOGS + filteredData.totalOpEx).toFixed(0)}
              </span>
            </div>
          </div>
        </div>

        {/* Category-wise COGS */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Category-wise COGS</h3>
          {filteredData.categoryWiseCOGS.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBar data={filteredData.categoryWiseCOGS}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} fontSize={12} />
                <YAxis />
                <Tooltip formatter={(value) => '₹' + value.toFixed(0)} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {filteredData.categoryWiseCOGS.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#f97316', '#ef4444', '#f59e0b', '#10b981', '#3b82f6'][index % 5]} />
                  ))}
                </Bar>
              </RechartsBar>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-500 py-12">No data available</p>
          )}
        </div>
      </div>

      {/* Insights & Recommendations */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-6 h-6" />
          <h3 className="text-xl font-bold">Business Insights</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredData.profitMargin >= 20 && (
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
              <p className="font-bold mb-1">✅ Healthy Profit Margin</p>
              <p className="text-sm">Your {filteredData.profitMargin.toFixed(1)}% profit margin is excellent!</p>
            </div>
          )}

          {filteredData.profitMargin < 10 && filteredData.profitMargin > 0 && (
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
              <p className="font-bold mb-1">⚠️ Low Profit Margin</p>
              <p className="text-sm">Consider reducing costs or increasing prices to improve margins.</p>
            </div>
          )}

          {filteredData.totalCOGS > filteredData.totalRevenue * 0.6 && filteredData.totalRevenue > 0 && (
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
              <p className="font-bold mb-1">📦 High COGS</p>
              <p className="text-sm">COGS is {((filteredData.totalCOGS/filteredData.totalRevenue)*100).toFixed(0)}% of revenue. Negotiate better prices with suppliers.</p>
            </div>
          )}

          {filteredData.avgOrderValue > 400 && (
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
              <p className="font-bold mb-1">💰 High Order Value</p>
              <p className="text-sm">Average order of ₹{filteredData.avgOrderValue.toFixed(0)} is great!</p>
            </div>
          )}

          {filteredData.netProfit < 0 && (
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
              <p className="font-bold mb-1">🚨 Action Required</p>
              <p className="text-sm">Urgent: Review pricing, reduce waste, optimize menu, and cut unnecessary expenses.</p>
            </div>
          )}

          {filteredData.totalOpEx > filteredData.grossProfit && filteredData.grossProfit > 0 && (
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
              <p className="font-bold mb-1">⚠️ High Operating Expenses</p>
              <p className="text-sm">OpEx exceeds gross profit. Review rent, salaries, and utilities.</p>
            </div>
          )}
        </div>
      </div>

      {/* Operating Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 md:p-8 w-full max-w-md my-8 max-h-[85vh] overflow-y-auto">    <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingExpense ? 'Edit' : 'Add'} {expenseType === 'salary' ? 'Employee Salary' : expenseType === 'rent' ? 'Rent' : 'Miscellaneous Expense'}
              </h2>
              <button onClick={() => {
                setShowExpenseModal(false);
                setEditingExpense(null);
              }} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSaveExpense} className="space-y-4">
              {expenseType === 'salary' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employee Name *</label>
                  <input
                    type="text"
                    value={expenseForm.employeeName}
                    onChange={(e) => setExpenseForm({ ...expenseForm, employeeName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="e.g., John Doe"
                  />
                </div>
              )}

              {expenseType !== 'salary' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                  <input
                    type="text"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder={expenseType === 'rent' ? 'e.g., Monthly Rent' : 'e.g., Electricity Bill'}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  min="0"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                <input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                <input
                  type="month"
                  value={expenseForm.month}
                  onChange={(e) => setExpenseForm({ ...expenseForm, month: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">For monthly recurring expenses</p>
              </div>

              {expenseType === 'salary' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="Optional notes..."
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    {editingExpense ? 'Update' : 'Add'} Expense
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitLoss;